import { Component, OnInit, ViewChild } from '@angular/core';
import { Modal } from '@app/core/types/modal-type';
import { TableComponent } from '@shared/components/table/table.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';
import { TableConfig } from '@shared/interfaces/table/table-config';
import {
  MeterDto,
  MeterFrameDto,
  MeterSearchAvailableDto,
  MeterSearchAvailableQueryDto,
  MeterService
} from '@app/api/nts';

import { MeteringPointService } from '@app/api/dmc';
import { meterSimpleTableConfig } from '@shared/table-configs/meter/meter-simple.table';
import { Observable, switchMap } from 'rxjs';
import { removeNullKeys } from '@app/core/utils/remove-null-keys';
import { ControlsOf, FormGroupType } from '@app/core/utils/formgroup-type';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { TranslatePipe } from '@ngx-translate/core';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { DatePipe, NgClass } from '@angular/common';
import { DATE_TIME_FORMAT } from '@app/core/constants/date-time-format';
import { DATE_FORMAT } from '@app/core/constants/date-format';
import { CircleCheck, LucideAngularModule } from 'lucide-angular';
import { CardComponent } from '@shared/components/card/card.component';
import { AlertService } from '@app/core/services/alert.service';
import { FormApiService } from '@app/core/services/form-api.service';
import { SortParam } from '@app/core/constants/sort-param';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-meter-replace-modal',
  imports: [
    ReactiveFormsModule,
    InputNumberDirective,
    TableComponent,
    InputGroupComponent,
    TranslatePipe,
    LabelDirective,
    InputDirective,
    ButtonDirective,
    InputDateDirective,
    NgClass,
    DatePipe,
    LucideAngularModule,
    CardComponent
  ],
  templateUrl: './meter-replace-modal.component.html'
})
export class MeterReplaceModalComponent implements Modal, OnInit, HasUnsavedChanges {
  @ViewChild('table', { static: false }) public table!: TableComponent<any>;

  /** Steppers **/
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public currentStep: number = 0;
  public meterReadingSkipped: boolean = false;

  /** Statics **/
  protected readonly DATE_TIME_FORMAT = DATE_TIME_FORMAT;
  protected readonly DATE_FORMAT = DATE_FORMAT;

  /** Icon **/
  protected readonly CircleCheck = CircleCheck;

  /** Steps **/
  public steps: { title: string, fn?: () => boolean, children?: string[] }[] = [
    { title: 'meterFrame.changeMeter.newMeter', fn: () => this.meterSelected() },
    { title: 'meterFrame.changeMeter.meterStand', fn: () => this.detailsValid() },
    { title: 'meterFrame.changeMeter.summary' }
  ];

  /** Form **/
  public form: FormGroup = new FormGroup({
    meterFrameId: new FormControl(null, Validators.required),
    currentMeter: new FormGroup({
      changeDate: new FormControl(new Date(), Validators.required),
      meterId: new FormControl(null, Validators.required),
      meterNumber: new FormControl(null, Validators.required),
      A14_measured: new FormControl(null, Validators.required),
      A23_measured: new FormControl(null, Validators.required),
      R12_measured: new FormControl(null, Validators.required),
      R34_measured: new FormControl(null, Validators.required),
    }),
    newMeter: new FormGroup({
      changeDate: new FormControl(new Date(), Validators.required),
      meterId: new FormControl(null, Validators.required),
      meterNumber: new FormControl(null, Validators.required),
      A14_measured: new FormControl(null, Validators.required),
      A23_measured: new FormControl(null, Validators.required),
      R12_measured: new FormControl(null, Validators.required),
      R34_measured: new FormControl(null, Validators.required),
    })
  });

  private readonly measurementFields = [
    'A14_measured',
    'A23_measured',
    'R12_measured',
    'R34_measured',
  ] as const;

  /** Search Form **/
  public searchForm: FormGroupType<MeterSearchAvailableQueryDto> = new FormGroup<ControlsOf<MeterSearchAvailableQueryDto>>({
    commissioningDate: new FormControl(),
    meterNumber: new FormControl(),
    alternativeMeterNumber: new FormControl(),
    serialNumber: new FormControl(),
  })

  /** Table **/
  public meterSimpleTableConfig: TableConfig<MeterSearchAvailableDto> = {
    ...meterSimpleTableConfig,
    id: 'meter-attach-replace-table',
    serviceCallback: (pageSize: number, page: number, sort?: SortParam) => this.handleSearch(pageSize, page, sort),
  }

  public selectedElements: MeterSearchAvailableDto[] = [];

  public meterFrameId?: string;
  public currentMeterFrame?: MeterFrameDto;
  public currentMeter?: MeterDto;

  constructor(
    private readonly meterService: MeterService,
    private readonly meteringPointService: MeteringPointService,
    private readonly alertService: AlertService,
    private readonly formApiService: FormApiService,
  ) {
  }

  public ngOnInit(): void {
    if (!this.currentMeterFrame) {
      this.alertService.open('modal', 'danger', 'meter.replaceAttach.noMeterFrame');
    }

    if (this.currentMeterFrame?.id) {
      this.form.get('meterFrameId')?.setValue(this.currentMeterFrame.id);
    }

    if (this.currentMeter?.id) {
      this.form.get('currentMeter.meterId')?.setValue(this.currentMeter.id);
    }

    if (this.currentMeter?.meterNumber) {
      this.form.get('currentMeter.meterNumber')?.setValue(this.currentMeter.meterNumber);
    }

    if (this.currentMeter?.id && !this.currentMeterFrame?.id) {
      this.alertService.open('modal', 'danger', 'meter.replaceAttach.noMeterFrame');
    }

    /** Subscribe to search form changes **/
    this.searchForm.get('commissioningDate')?.valueChanges.subscribe((value: any) => {
      this.form.get('newMeter.changeDate')?.setValue(value);

      if (this.meterReadingSkipped) {
        this.form.get('currentMeter.changeDate')?.setValue(value);
      }
    })

    // TODO(sam.olesen) Temporarily set simple meter change as default until precise meter change is fixed
    this.setMeterReadingSkipped(true);
  }

  /** Handle Search **/
  public handleSearch(pageSize: number, page: number, sort?: SortParam): Observable<any> {
    const query = removeNullKeys(this.searchForm.value);
    return this.meterService.meterSearchAvailablePost({ ...query, pageSize, page, filterDiscarded: true } as MeterSearchAvailableQueryDto);
  }

  public meterSelected(): boolean {
    return this.selectedElements.length === 1;
  }

  public detailsValid(): boolean {
    return this.form.valid;
  }

  public setMeterReadingSkipped(skipped: boolean): void {
    this.meterReadingSkipped = skipped;

    for (const groupName of ['currentMeter', 'newMeter'] as const) {
      for (const field of this.measurementFields) {
        const control = this.form.get(`${groupName}.${field}`);
        skipped
          ? control?.disable({ emitEvent: false })
          : control?.enable({ emitEvent: false });
      }
    }
  }

  public changeSelectedMeter(meter: MeterSearchAvailableDto[]): void {
    if (!meter || meter?.length !== 1) return;

    this.form.get('newMeter.meterId')?.setValue(meter[0].id);
    this.form.get('newMeter.meterNumber')?.setValue(meter[0].meterNumber);
  }

  public submit(): void {
    if (this.meterReadingSkipped) {
      this.formApiService.submit(
        this.form,
        (data) =>
          this.meteringPointService.meteringPointValidateChangeMeterPost({
            newMeterId: data.meterId,
            oldMeterId: data.oldMeterId,
            newMeterCommissioningDate: data.commissioningDate,
          }).pipe(switchMap(() => this.meterService.meterReplacePost(data))),
        {
          preprocess: () => ({
            meterId: this.form.get('newMeter.meterId')?.value,
            oldMeterId: this.form.get('currentMeter.meterId')?.value,
            meterFrameId: this.form.get('meterFrameId')?.value,
            commissioningDate: this.searchForm.get('commissioningDate')?.value,
          }),
          closeModal: true,
          successMessage: 'meter.replaceAttach.replace.success',
        }
      );

      return;
    }

    this.formApiService.submit(
      this.form,
      (data) => this.meterService.meterMeterChangePost(data),
      {
        preprocess: () => ({
          meterFrameId: this.form.get('meterFrameId')?.value,
          currentMeter: {
            ...this.form.get('currentMeter')?.value,
            changeDate: new Date(this.form.get('currentMeter.changeDate')?.value).toISOString(),
          },
          newMeter: {
            ...this.form.get('newMeter')?.value,
            changeDate: new Date(this.form.get('newMeter.changeDate')?.value).toISOString(),
          },
        }),
        closeModal: true,
        successMessage: 'meter.replaceAttach.replace.success',
      }
    )
  }
}
